import { Award, Flame, type LucideIcon, TrendingUp, Video } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Skeleton } from "@/components/ui/skeleton"
import { MOTION_BEATS } from "@/lib/motion"
import { formatRelativeTime } from "@/lib/relative-time"
import { cn } from "@/lib/utils"
import type { CrewPulseEvent, CrewPulseEventKind } from "@/types/collab"

/**
 * OKS-473 — per-kind icon-badge config for a Crew Pulse row. A SIBLING to the
 * notifications `NOTIFICATION_TYPE_CONFIG` (OKS-398) — deliberately NOT shared:
 * pulse rows are not notifications (no read-state, no PATCH, a different kind
 * set), so coupling them would risk regressing the notifications surface (TRD
 * KD-3). Each kind maps to a positively-tinted Lucide icon (`lucide-react`
 * canonical icons — never hand-crafted path data). Colour is never the sole
 * differentiator: every badge carries a localized `aria-label`
 * (`crew_pulse.type.<kind>`) AND a distinct icon shape (UX Spec §6).
 */
const PULSE_KIND_CONFIG: Record<
  CrewPulseEventKind,
  { icon: LucideIcon; badgeClass: string }
> = {
  posted: {
    icon: Video,
    badgeClass: "bg-primary/10 text-primary",
  },
  badge_awarded: {
    icon: Award,
    badgeClass:
      "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400",
  },
  kudos: {
    icon: Flame,
    badgeClass:
      "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400",
  },
  rank_up: {
    icon: TrendingUp,
    badgeClass:
      "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400",
  },
}

/** First grapheme of the first name, upper-cased — the avatar monogram. Blank
 *  first name (PII edge) ⇒ empty, and `<AvatarFallback>` renders nothing
 *  visible (an avatar-only row, UX Spec §3 edge cases). */
function monogram(firstName: string): string {
  return firstName.trim().charAt(0).toUpperCase()
}

interface PulseRowProps {
  event: CrewPulseEvent
  /** When true, plays the one-shot `live-pulse` arrival beat (fade + 16px
   *  y-settle) on mount — used by the stream for freshly-prepended events
   *  (UX Spec §1.2). */
  isNew?: boolean
}

/**
 * OKS-473 — a single Crew Pulse event row (UX Spec §2.3).
 *
 * Layout: member avatar with a per-kind icon-badge overlay · a bold action
 * title ("Maya posted a clip") · an optional secondary context line (the
 * mission / badge / rank name from `payload.title`) · an uppercase relative
 * time. First name + avatar ONLY — the row renders pre-scoped, brand-internal
 * data from `CrewPulseQuery`; it never fetches or re-derives identity.
 *
 * Not interactive in V1 (no deep-link target — PRD "out of scope"), so it is a
 * plain `<li>`, not a button.
 */
export function PulseRow({ event, isNew = false }: PulseRowProps) {
  const { t, i18n } = useTranslation()
  const { icon: KindIcon, badgeClass } = PULSE_KIND_CONFIG[event.kind]
  const title = t(`crew_pulse.event.${event.kind}`, { name: event.first_name })
  const secondary = event.payload?.title

  return (
    <li
      data-testid={`pulse-row-${event.id}`}
      data-pulse-kind={event.kind}
      className={cn(
        "flex items-start gap-3 px-4 py-3",
        isNew && MOTION_BEATS["live-pulse"],
      )}
    >
      <div className="relative shrink-0">
        <Avatar className="size-10 rounded-full">
          <AvatarImage src={event.avatar_url ?? undefined} alt="" />
          <AvatarFallback className="bg-muted text-muted-foreground text-sm font-semibold">
            {monogram(event.first_name)}
          </AvatarFallback>
        </Avatar>
        <span
          data-testid={`pulse-row-${event.id}-badge`}
          aria-label={t(`crew_pulse.type.${event.kind}`)}
          className={cn(
            "border-background absolute -right-1 -bottom-1 flex size-5 items-center justify-center rounded-full border-2",
            badgeClass,
          )}
        >
          <KindIcon aria-hidden="true" className="size-3" />
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm leading-tight font-semibold">{title}</p>
        {secondary ? (
          <p className="text-muted-foreground truncate text-xs">{secondary}</p>
        ) : null}
        <time
          dateTime={event.occurred_at}
          className="text-muted-foreground/80 mt-0.5 text-[11px] tracking-wide uppercase"
        >
          {formatRelativeTime(event.occurred_at, t, i18n.language)}
        </time>
      </div>
    </li>
  )
}

/**
 * OKS-473 — loading skeleton matching the populated `PulseRow` layout (avatar
 * circle + two text lines). A skeleton, NOT a spinner (UX Spec §3).
 */
export function PulseRowSkeleton() {
  return (
    <li
      className="flex items-start gap-3 px-4 py-3"
      data-testid="pulse-row-skeleton"
    >
      <Skeleton className="size-10 shrink-0 rounded-full" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 py-0.5">
        <Skeleton className="h-3.5 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </li>
  )
}
